import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Field, FieldArray, Form, Formik, FormikHelpers } from 'formik'
import { makeStyles, ThemeProvider } from '@material-ui/core/styles'
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@material-ui/core'
import { Close as CloseIcon } from '@material-ui/icons'
import { muiTheme } from 'theme'

import DraggablePaper from 'civic-champs-shared/core/confirm-dialog/DraggablePaper'
import { ContainedButton, OutlinedButton } from 'civic-champs-shared/core'
import { AddressField, CustomSwitch, StyledOutlinedTextField } from 'civic-champs-shared/formik/components'

import { LocationAddressType, LocationType, OrganizationLocationReporting } from '../types'
import { yup } from 'civic-champs-shared/api/utils'
import { Geofencing } from 'civic-champs-shared/core/location/utils'
import { EventGeofencing, mapEventGeofencingToGeofencing, mapGeofencingToEventGeofencing } from 'utils/event'
import { DEFAULT_GEOFENCING } from 'civic-champs-shared/constants/GEO_DATA'
import { useFetchLocations } from 'locations/hooks'
import { useFeatureEnabled } from '../../core/feature/hooks'
import { useCurrentOrg } from '../../auth/hooks'
import { getOrgReferenceCoordinates } from '../helpers/org-reference-coordinates'
import cn from 'classnames'
import { StyledAutocomplete } from '../../components/StyledAutocomplete'
import { AssociationType, EventGroup } from '../../Event/interfaces/interfaceCreateEditEvent'
import { useEnabledGroups, useOptions } from '../../Event/components/opportunity/GroupPicker/hooks'
import { useGroups } from '../../group/hooks/useGroups'
import { Group } from '../../Event/components/opportunity/GroupPicker/types'
import { Theme as DefaultTheme } from '@material-ui/core/styles/createMuiTheme'
import { CkEditorStyledField } from '../../civic-champs-shared/formik/components/CkEditorStyledField'
import {
  StyledKeyboardDatePicker,
  StyledKeyboardTimePicker,
} from '../../civic-champs-shared/formik/components/StyledFormikDateTimePickers'
import moment from 'moment'
import RadioGroupFormField from '../../components/RadioGroupFormField'
import { StyledInput } from '../../civic-champs-shared/formik/components/StyledInput'
import { ArraySchema, ObjectSchema } from 'yup'
import { useDateTimeUtils } from '../../civic-champs-shared/utils/useDateTimeUtils'
import DeleteIcon from '@material-ui/icons/Delete'
import AdvancedAutocomplete from 'civic-champs-shared/core/AdvancedAutocomplete'
import { Role } from 'volunteer-role/types'
import { filter, isNull } from 'lodash'
import AddCircleIcon from '@material-ui/icons/AddCircle'
import { InlineAddButton } from 'civic-champs-shared/core/InlineAddButton'
import { useRolesCollection } from 'volunteer-role/hooks'
import Loading from 'civic-champs-shared/core/Loading'
import { PlusCodeField } from 'civic-champs-shared/formik/components/PlusCodeField'
import {
  deriveAddressTypeFromLocation,
  EMPTY_GEOCODED_FIELD,
  buildGeocodedFieldFromLocation,
  extractPlusCode,
  GeocodedAddressFormValue,
  hasGeocodedLocation,
  mapLocationAddressFormToPayload,
  resolveLocationDisplayName,
} from '../helpers/location-address'
import { resolvePlusCodeToCoordinates } from '../../civic-champs-shared/core/location/plus-code-utils'
import { useClearLocationFieldsOnTypeChange } from '../hooks/useClearLocationFieldsOnTypeChange'

export interface AddEditLocationModalProps {
  location?: OrganizationLocationReporting
  showing: boolean
  complete: (value: any) => void
  close: () => void
  forceEnableCheckIns?: true
}

const useStyles = makeStyles<DefaultTheme, { offersEnabled?: boolean }>({
  title: {
    cursor: 'move',
    padding: '20px 0 0',
    fontFamily: 'Poppins',
    fontWeight: 500,
    fontSize: '24px',
    lineHeight: '32px',
    color: '#000000',
  },
  dialog: {
    background: '#F8FAFF',
    padding: '0 20px 20px',
    position: 'relative',
    maxWidth: '100%',
    width: ({ offersEnabled }) => (offersEnabled ? '700px' : '415px'),
  },
  dialogCloseButton: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  dialogCloseIcon: {
    fontSize: '16px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0 0',
  },
  content: {
    padding: '8px 0',
  },
  switch: {
    display: 'flex',
    margin: '0 0 10px 4px',
    gap: '5px',
    '& > span': {
      fontFamily: 'Nunito',
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: '13px',
      lineHeight: '16px',
      letterSpacing: '0.4px',
      color: '#000000',
    },
  },
  subItem: {
    marginTop: ({ offersEnabled }) => (offersEnabled ? 0 : '12px'),
    marginLeft: '40px',
    maxWidth: 'calc(100% - 40px)',
  },
  section: {
    gap: '10px',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: ({ offersEnabled }) => (offersEnabled ? '10px' : 0),
    '&:last-child': {
      marginBottom: '14px',
    },
  },
  sectionTitle: {
    fontFamily: 'Nunito',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.5px',
    marginBottom: ({ offersEnabled }) => (offersEnabled ? '-5px' : '5px'),
    '&$noMargin': {
      marginBottom: 0,
    },
  },
  subSection: {
    '& $dateTimePickers, & $subItem': {
      marginLeft: '40px',
      maxWidth: 'calc(100% - 40px)',
    },
  },
  noMargin: {},
  groupsInfo: {
    fontFamily: 'Nunito',
    fontSize: ({ offersEnabled }) => (offersEnabled ? '14px' : '16px'),
    fontWeight: 400,
    lineHeight: ({ offersEnabled }) => (offersEnabled ? '22px' : '24px'),
    letterSpacing: '0.5px',
    textAlign: 'left',
    marginTop: ({ offersEnabled }) => (offersEnabled ? '-6px' : '14px'),
  },
  dateTimePickers: {
    display: 'flex',
    gap: '24px',
    width: '440px',
  },
  dateTimePicker: {
    width: '208px',
  },
  unGap: {
    marginTop: '-10px',
  },
  role: {
    display: 'flex',
    flexDirection: 'row-reverse',
    width: '100%',
    gap: '10px',
    alignItems: 'center',
  },
  roleSelector: {
    flexGrow: 2,
  },
  roleDelete: {
    cursor: 'pointer',
    flexShrink: 0,
  },
  addIcon: {
    fontSize: '16px',
    color: '#0F5DB5',
    marginRight: '8px',
  },
})

interface LocationFormData {
  name: string
  address?: GeocodedAddressFormValue
  plusCode?: GeocodedAddressFormValue
  enableCheckIns?: boolean
  locationDetails?: string | null
  description?: string
  type?: LocationType
  addressType?: LocationAddressType
  visibilityGroups?: EventGroup[]
  onboardingGroups?: EventGroup[]
  startDate?: moment.Moment
  endDate?: moment.Moment | null
  startTime?: moment.Moment
  endTime?: moment.Moment | null
  locationIsAddress?: boolean
  roles?: (number | null)[]
  autoCheckoutTimes?: moment.Moment[]
}

const DEFAULT_AUTO_CHECKOUT_TIME = '23:59:59'

const toOrgWallClockTimeForForm = (
  time: string,
  timeZone: string,
  fakeAsLocalTimeForUI: (dateTime: Date | moment.Moment | string) => moment.Moment,
) => {
  const [hours, minutes, seconds = '0'] = time.split(':')
  const paddedTime = `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}`
  return fakeAsLocalTimeForUI(moment.tz(`2000-01-01 ${paddedTime}`, 'YYYY-MM-DD HH:mm:ss', timeZone))
}

const parseAutoCheckoutTimesForForm = (
  times: string[] | undefined,
  timeZone: string,
  fakeAsLocalTimeForUI: (dateTime: Date | moment.Moment | string) => moment.Moment,
) =>
  (times?.length ? times : [DEFAULT_AUTO_CHECKOUT_TIME]).map(time =>
    toOrgWallClockTimeForForm(time, timeZone, fakeAsLocalTimeForUI),
  )

const formatAutoCheckoutTimesForApi = (
  times: moment.Moment[] = [],
  unfakeAsLocalTimeForUI: (dateTime: Date | moment.Moment) => moment.Moment,
) => times.map(time => unfakeAsLocalTimeForUI(time).format('HH:mm:ss'))

const resolveCheckInVisibilityType = (type: LocationType | undefined, enableCheckIns?: boolean): LocationType => {
  if (!enableCheckIns) {
    return LocationType.Event
  }

  return type === LocationType.Private ? LocationType.Private : LocationType.Public
}

const LocationAddressTypeFieldSync = ({
  addressType,
  setFieldValue,
}: {
  addressType?: LocationAddressType
  setFieldValue: FormikHelpers<LocationFormData>['setFieldValue']
}) => {
  useClearLocationFieldsOnTypeChange(addressType, setFieldValue)
  return null
}

const EnableCheckInsVisibilityTypeSync = ({
  enableCheckIns,
  type,
  setFieldValue,
}: {
  enableCheckIns?: boolean
  type?: LocationType
  setFieldValue: FormikHelpers<LocationFormData>['setFieldValue']
}) => {
  useEffect(() => {
    if (enableCheckIns && type === LocationType.Event) {
      setFieldValue('type', LocationType.Public)
    }
  }, [enableCheckIns, type, setFieldValue])

  return null
}

const AddEditLocationDialog = ({
  location,
  showing,
  close,
  complete,
  forceEnableCheckIns,
}: AddEditLocationModalProps) => {
  const { unfakeAsLocalTimeForUIAsString, unfakeAsLocalTimeForUI, fakeAsLocalTimeForUI, compileDateTime } =
    useDateTimeUtils()
  const { fetchLocations } = useFetchLocations()
  const offersEnabled = useFeatureEnabled('StandaloneOffers')
  const plusCodesEnabled = useFeatureEnabled('PlusCodes')
  const org = useCurrentOrg()
  const referenceCoordinates = useMemo(() => getOrgReferenceCoordinates(org), [org])
  const [screen, setScreen] = useState(0)
  const { groups: initialGroups } = useGroups() as { groups: Group[]; loading: boolean }
  const groups = useEnabledGroups(initialGroups)
  const options = useOptions({ groups, associationType: AssociationType.EVENT_PRIVATE_TO_MEMBERS, value: [] })
  const onboardingOptions = useOptions({
    groups,
    associationType: AssociationType.ADD_PARTICIPANTS_TO_GROUP,
    value: [],
  })
  const [{ roles, initiallyLoaded: rolesLoaded }, { addRole }] = useRolesCollection({
    reporting: false,
    forLocations: true,
  })
  const handleSubmit = ({
    address,
    plusCode,
    name,
    description,
    type,
    visibilityGroups,
    onboardingGroups,
    enableCheckIns,
    locationDetails,
    startDate,
    endDate,
    startTime,
    endTime,
    addressType,
    roles,
    autoCheckoutTimes,
  }: LocationFormData) => {
    const resolvedName = resolveLocationDisplayName({ name, locationDetails, addressType })
    const resolvedLocationDetails =
      addressType === LocationAddressType.PlusCode ? resolvedName || null : locationDetails

    const locationPayload = mapLocationAddressFormToPayload({
      addressType,
      address,
      plusCode,
      locationDetails: resolvedLocationDetails,
    })
    const geofencing =
      (locationPayload as { geofencing?: Geofencing }).geofencing ||
      (addressType === LocationAddressType.PlusCode ? plusCode?.geofencing : address?.geofencing) ||
      DEFAULT_GEOFENCING
    complete({
      id: location?.id,
      ...locationPayload,
      name: resolvedName,
      geofencing: mapGeofencingToEventGeofencing(geofencing),
      description,
      type: resolveCheckInVisibilityType(type, enableCheckIns),
      visibilityGroups,
      onboardingGroups,
      startsAt:
        startDate && startTime ? unfakeAsLocalTimeForUIAsString(compileDateTime(startDate, startTime)) : undefined,
      endsAt: endDate && endTime ? unfakeAsLocalTimeForUIAsString(compileDateTime(endDate, endTime)) : undefined,
      roleIds: (roles || []).filter(r => !isNull(r)),
      autoCheckoutTimes: formatAutoCheckoutTimesForApi(autoCheckoutTimes, unfakeAsLocalTimeForUI),
    })
  }

  const initialValues = useMemo(() => {
    const roles = location?.roleIds?.length ? location.roleIds : [null]
    const addressType = deriveAddressTypeFromLocation(location)
    const geocodedField = buildGeocodedFieldFromLocation(location, geofencing =>
      mapEventGeofencingToGeofencing(geofencing as EventGeofencing),
    )
    const address = addressType === LocationAddressType.AddressAndCoords ? geocodedField : EMPTY_GEOCODED_FIELD
    const plusCode = addressType === LocationAddressType.PlusCode ? geocodedField : EMPTY_GEOCODED_FIELD
    return {
      name: location?.name || '',
      address,
      plusCode,
      addressType,
      ...(offersEnabled
        ? {
            type: location?.type || LocationType.Public,
            visibilityGroups: location?.visibilityGroups || [],
            onboardingGroups: location?.onboardingGroups || [],
            description: location?.description || '',
            enableCheckIns: location?.type ? location?.type !== LocationType.Event : false,
            locationDetails:
              addressType === LocationAddressType.PlusCode
                ? location?.locationDetails || location?.name || ''
                : location?.locationDetails || null,
            startDate: location?.startsAt ? fakeAsLocalTimeForUI(location?.startsAt) : moment(),
            startTime: location?.startsAt ? fakeAsLocalTimeForUI(location?.startsAt) : moment(),
            endDate: location?.endsAt ? fakeAsLocalTimeForUI(location?.endsAt) : null,
            endTime: location?.endsAt ? fakeAsLocalTimeForUI(location?.endsAt) : null,
            roles,
            autoCheckoutTimes: parseAutoCheckoutTimesForForm(
              location?.autoCheckoutTimes,
              org.timeZone,
              fakeAsLocalTimeForUI,
            ),
          }
        : {}),
    }
  }, [location, offersEnabled, fakeAsLocalTimeForUI, org.timeZone]) as LocationFormData

  const schema = useMemo(
    () =>
      yup.object({
        name: yup.string().test('nameRequired', 'Location Name is Required', function (value) {
          const { addressType } = this.parent as LocationFormData
          if (addressType === LocationAddressType.PlusCode) {
            return true
          }
          return !!value?.trim()
        }),
        address: yup
          .mixed()
          .when('addressType', (addressType: LocationAddressType, schema: ArraySchema<ObjectSchema>) =>
            addressType === LocationAddressType.AddressAndCoords
              ? yup
                  .object({
                    line1: yup
                      .string()
                      .min(1, 'An address is required. Please enter an address.')
                      .required('An address is required. Please enter an address.')
                      .typeError('An address is required. Please enter an address.'),
                  })
                  .required('An address is required. Please enter an address.')
                  .typeError('An address is required. Please enter an address.')
              : schema,
          ),
        plusCode: yup
          .mixed()
          .when('addressType', (addressType: LocationAddressType, schema: ArraySchema<ObjectSchema>) =>
            addressType === LocationAddressType.PlusCode
              ? yup
                  .object({
                    line1: yup.string(),
                  })
                  .test(
                    'plusCodeRequired',
                    'A Plus Code is required. Please enter a Plus Code.',
                    (value: LocationFormData['plusCode']) => !!value?.line1?.trim(),
                  )
              : schema,
          ),
        locationDetails: yup.mixed().when('addressType', (addressType: LocationAddressType) => {
          if (addressType === LocationAddressType.CustomTextOnly) {
            return yup
              .string()
              .min(1, 'A location is required. Please enter a location.')
              .required('A location is required. Please enter a location.')
              .typeError('A location is required. Please enter a location.')
          }

          if (addressType === LocationAddressType.PlusCode) {
            return yup
              .string()
              .nullable()
              .test(
                'locationDetailsRequired',
                'A location is required. Please enter a location.',
                (value: string | null) => !!value?.trim(),
              )
          }

          return yup.string().notRequired().nullable()
        }),
        roles: yup.mixed().when('enableCheckIns', (enableCheckIns: boolean) => {
          if (enableCheckIns) {
            return yup.mixed().test('isProperRoles', 'Please select at least one volunteering Role', value => {
              return value.filter((v: number | null) => !isNull(v)).length > 0
            })
          } else {
            return yup.mixed().notRequired()
          }
        }),
        autoCheckoutTimes: yup.mixed().when('enableCheckIns', (enableCheckIns: boolean) => {
          if (enableCheckIns) {
            return yup
              .array()
              .of(yup.mixed())
              .min(1, 'At least one auto checkout time is required')
              .test('validTimes', 'Please enter a valid checkout time', value => !!value?.length)
          }

          return yup.mixed().notRequired()
        }),
      }),
    [],
  )

  const findDuplicate = useCallback(
    async (search: Record<string, unknown>): Promise<boolean> => {
      try {
        const data = await fetchLocations({ search: JSON.stringify(search) })
        const duplicates = location?.id ? data.filter(({ id }) => id !== location.id) : data
        return !!duplicates.length
      } catch {
        return false
      }
    },
    [fetchLocations, location],
  )

  const findDuplicateError = useCallback(
    async (values: LocationFormData): Promise<{ field: string; message: string } | null> => {
      const { addressType, address, plusCode } = values
      const nameField = addressType === LocationAddressType.PlusCode ? 'locationDetails' : 'name'
      const nameToCheck = resolveLocationDisplayName(values)
      const initialNameToCheck = resolveLocationDisplayName(initialValues)

      if (nameToCheck && nameToCheck !== initialNameToCheck) {
        if (await findDuplicate({ name: nameToCheck })) {
          return { field: nameField, message: 'This name has already been used. Please enter a unique name.' }
        }
      }

      if (addressType === LocationAddressType.AddressAndCoords && address?.line1) {
        const { lat, lng } = address.geofencing.location.coordinates
        const initial = initialValues.address
        const unchanged =
          initial &&
          address.line1 === initial.line1 &&
          lat === initial.geofencing.location.coordinates.lat &&
          lng === initial.geofencing.location.coordinates.lng

        if (!unchanged && (await findDuplicate({ location: { line1: address.line1, coordinates: [lng, lat] } }))) {
          return {
            field: 'address',
            message: 'This address has already been saved. Please enter a different address.',
          }
        }
      }

      if (addressType === LocationAddressType.PlusCode && plusCode?.line1) {
        const code = extractPlusCode(plusCode.line1)
        const { lat, lng } = plusCode.geofencing.location.coordinates
        const initial = initialValues.plusCode
        const unchanged =
          initial &&
          code === extractPlusCode(initial.line1) &&
          lat === initial.geofencing.location.coordinates.lat &&
          lng === initial.geofencing.location.coordinates.lng

        if (!unchanged && (await findDuplicate({ location: { line1: code, coordinates: [lng, lat] } }))) {
          return {
            field: 'plusCode',
            message: 'This Plus Code has already been saved. Please enter a different Plus Code.',
          }
        }
      }

      return null
    },
    [findDuplicate, initialValues],
  )

  const classes = useStyles({ offersEnabled })

  if (!rolesLoaded) {
    return <Loading />
  }
  return (
    // @ts-ignore
    <Dialog
      open={showing}
      // @ts-ignore
      PaperComponent={DraggablePaper}
      // @ts-ignore
      PaperProps={{ handle: `#location-prompt` }}
      aria-labelledby="draggable-dialog-title"
      maxWidth="xs"
      classes={{ paper: classes.dialog }}
      disableEnforceFocus
    >
      <Formik initialValues={initialValues} validationSchema={schema} onSubmit={handleSubmit} validateOnChange>
        {({
          submitForm,
          isSubmitting,
          values,
          setFieldValue,
          validateForm,
          setTouched,
          errors,
          touched,
          setFieldTouched,
          setFieldError,
        }) => {
          const next = async () => {
            let resolvedValues = values

            if (values.addressType === LocationAddressType.PlusCode && values.plusCode?.line1?.trim()) {
              const result = await resolvePlusCodeToCoordinates({
                input: values.plusCode.line1,
                referenceCoordinates,
              })

              if ('error' in result) {
                setFieldTouched('plusCode', true, false)
                setFieldError('plusCode', result.error)
                return
              }

              const plusCode = {
                ...values.plusCode,
                line1: values.plusCode.line1.trim(),
                ...result.address,
                geofencing: {
                  ...(values.plusCode.geofencing || DEFAULT_GEOFENCING),
                  location: {
                    coordinates: { lat: result.lat, lng: result.lng },
                    type: 'Point' as const,
                  },
                },
              }
              resolvedValues = { ...values, plusCode }
              await setFieldValue('plusCode', plusCode)
            }

            const validationErrors = await validateForm(resolvedValues)
            if (Object.keys(validationErrors).length > 0) {
              setTouched(
                Object.keys(validationErrors).reduce<Record<string, boolean>>((acc, key) => {
                  acc[key] = true
                  return acc
                }, {}),
                false,
              )
              return
            }

            const duplicate = await findDuplicateError(resolvedValues)
            if (duplicate) {
              setFieldTouched(duplicate.field, true, false)
              setFieldError(duplicate.field, duplicate.message)
              return
            }

            if (screen === 0 && values.enableCheckIns) {
              setScreen(1)
            } else {
              submitForm()
            }
          }
          const selectedRoles = new Set([...(values.roles || [])])
          const handleAdd = async (index: number) => {
            const role = await addRole()
            setFieldValue('roles', (values.roles as any).toSpliced(index, 1, { id: role.id }))
          }
          return (
            <Form>
              <LocationAddressTypeFieldSync addressType={values.addressType} setFieldValue={setFieldValue} />
              <EnableCheckInsVisibilityTypeSync
                enableCheckIns={values.enableCheckIns}
                type={values.type}
                setFieldValue={setFieldValue}
              />
              <DialogTitle classes={{ root: classes.title }} disableTypography={true} id="location-prompt">
                {screen === 1 ? 'Additional Information' : location ? 'Edit Location' : 'Add Location'}
                <IconButton className={classes.dialogCloseButton} onClick={() => close()}>
                  <CloseIcon className={classes.dialogCloseIcon} />
                </IconButton>
              </DialogTitle>
              <DialogContent classes={{ root: classes.content }}>
                {
                  [
                    <Fragment key={0}>
                      {(!offersEnabled || values.addressType !== LocationAddressType.PlusCode) && (
                        <Field
                          component={StyledOutlinedTextField}
                          id="name"
                          name="name"
                          label="Location Name*"
                          placeholder="Enter a Location Name here…"
                          variant="outlined"
                          fullWidth
                        />
                      )}
                      {offersEnabled ? ( //TODO why "Offers", this doesn't seem to be related to "Offers", pick something else?
                        <>
                          <div className={cn(classes.section, classes.subSection)}>
                            <span className={classes.sectionTitle}>Location:</span>
                            <RadioGroupFormField
                              name="addressType"
                              disabled={isSubmitting || !!location?.timesUsedTotal}
                              options={[
                                {
                                  label: 'This Location is tied to a Google Maps address',
                                  value: LocationAddressType.AddressAndCoords,
                                },
                              ]}
                            />
                            {values.addressType == LocationAddressType.AddressAndCoords && (
                              <Field
                                className={classes.subItem}
                                fullWidth
                                variant="outlined"
                                labelShrink
                                label="Location Address *"
                                placeholder="Select your Location’s Google Maps address"
                                component={AddressField}
                                name="address"
                                showRadius
                                vertical
                                halfMap
                                hideMap={!values.address?.line1}
                                compactSlider
                                autocompleteDisabled={!!location?.timesUsedTotal}
                              />
                            )}
                            {plusCodesEnabled && (
                              <>
                                <div
                                  className={cn({
                                    [classes.unGap]: values.addressType != LocationAddressType.AddressAndCoords,
                                  })}
                                >
                                  <RadioGroupFormField
                                    name="addressType"
                                    disabled={isSubmitting || !!location?.timesUsedTotal}
                                    options={[
                                      {
                                        label: 'This Location is tied to a Plus Code',
                                        value: LocationAddressType.PlusCode,
                                      },
                                    ]}
                                  />
                                </div>
                                {values.addressType == LocationAddressType.PlusCode && (
                                  <>
                                    <Field
                                      component={StyledInput}
                                      name="locationDetails"
                                      className={classes.subItem}
                                      label={'Location Name*'}
                                      placeholder="Enter the name of your location"
                                      disabled={isSubmitting || !!location?.timesUsedTotal}
                                      maxLength={200}
                                    />
                                    <Field
                                      component={PlusCodeField}
                                      name="plusCode"
                                      referenceCoordinates={referenceCoordinates}
                                      className={classes.subItem}
                                      label={'Plus Code*'}
                                      placeholder="Enter the Plus Code for your location"
                                      disabled={isSubmitting || !!location?.timesUsedTotal}
                                      hideMap={!values?.plusCode?.line1}
                                    />
                                  </>
                                )}
                              </>
                            )}
                            <div
                              className={cn({
                                [classes.unGap]:
                                  (plusCodesEnabled && values.addressType != LocationAddressType.PlusCode) ||
                                  (!plusCodesEnabled && values.addressType == LocationAddressType.CustomTextOnly),
                              })}
                            >
                              <RadioGroupFormField
                                name="addressType"
                                disabled={isSubmitting || !!location?.timesUsedTotal}
                                options={[
                                  {
                                    label: 'This Location does not have a physical address',
                                    value: LocationAddressType.CustomTextOnly,
                                  },
                                ]}
                              />
                            </div>
                            {values.addressType == LocationAddressType.CustomTextOnly && (
                              <Field
                                component={StyledInput}
                                name="locationDetails"
                                className={classes.subItem}
                                label={'Location*'}
                                placeholder="Enter a location here…"
                                disabled={isSubmitting || !!location?.timesUsedTotal}
                                maxLength={100}
                              />
                            )}
                          </div>

                          <div className={cn(classes.section, classes.noMargin, classes.subSection)}>
                            <span className={classes.sectionTitle}>Visibility:</span>
                            <div className={classes.switch}>
                              <Field
                                component={CustomSwitch}
                                disabled={forceEnableCheckIns}
                                id="enableCheckIns"
                                name="enableCheckIns"
                                thumbColor="#FFF"
                                trackColor={values.enableCheckIns ? '#0F5DB5' : '#8E9192'}
                                type="checkbox"
                                tooltip="Roles set to Inactive will not be available for selection when adding new Events or Activities."
                              />
                              <span>Allow volunteers to check in via Civic Champs mobile app</span>
                            </div>
                            {values.enableCheckIns && (
                              <>
                                <div className={classes.dateTimePickers}>
                                  <div className={classes.dateTimePicker}>
                                    <Field
                                      inputVariant="outlined"
                                      label="Start Date *"
                                      higher
                                      component={StyledKeyboardDatePicker}
                                      name="startDate"
                                    />
                                  </div>

                                  <div className={classes.dateTimePicker}>
                                    <Field
                                      higher
                                      inputVariant="outlined"
                                      label="End Date"
                                      component={StyledKeyboardDatePicker}
                                      name="endDate"
                                      minDate={moment(values.startDate || undefined)
                                        .startOf('day')
                                        .toDate()}
                                      minDateMessage={'The end date cannot occur before the start date.'}
                                      clearable
                                    />
                                  </div>
                                </div>

                                <div className={classes.dateTimePickers}>
                                  <div className={classes.dateTimePicker}>
                                    <Field
                                      inputVariant="outlined"
                                      label="Start Time *"
                                      higher
                                      component={StyledKeyboardTimePicker}
                                      name="startTime"
                                    />
                                  </div>

                                  <div className={classes.dateTimePicker}>
                                    <Field
                                      higher
                                      inputVariant="outlined"
                                      label="End Time"
                                      component={StyledKeyboardTimePicker}
                                      name="endTime"
                                      clearable
                                    />
                                  </div>
                                </div>

                                <div className={classes.section}>
                                  <span className={classes.sectionTitle}>Roles:</span>
                                  <FieldArray name="roles">
                                    {({ remove, push, replace }) => (
                                      <div className={classes.section}>
                                        {values.roles?.map((role, index) => (
                                          <div className={classes.role} key={index}>
                                            <DeleteIcon
                                              className={classes.roleDelete}
                                              onClick={() => {
                                                ;(values.roles?.length || 0) > 1 ? remove(index) : replace(index, null)
                                              }}
                                            />
                                            <div className={classes.roleSelector}>
                                              <AdvancedAutocomplete<Role>
                                                label={'Role*'}
                                                placeholder={'Select a Role'}
                                                options={filter(
                                                  roles,
                                                  ({ id, isActive }) =>
                                                    (isActive && !selectedRoles.has(id)) || id === role,
                                                )}
                                                value={roles.find(({ id }) => id === role)}
                                                getOptionInfo={option => ({
                                                  left: option.name,
                                                  id: option.id,
                                                })}
                                                onBlur={() => setFieldTouched('roles', true)}
                                                onAdd={() => handleAdd(index)}
                                                addNewText={'Add a new role'}
                                                addNewIcon={<AddCircleIcon className={classes.addIcon} />}
                                                onChange={option => replace(index, option ? (option as Role).id : null)}
                                                error={touched.roles ? errors.roles : undefined}
                                              />
                                            </div>
                                          </div>
                                        ))}
                                        <InlineAddButton
                                          onClick={() => push({ id: null, quantity: null })}
                                          text="Add Role"
                                        />
                                      </div>
                                    )}
                                  </FieldArray>
                                </div>
                              </>
                            )}
                          </div>
                        </>
                      ) : (
                        <Field
                          fullWidth
                          variant="outlined"
                          labelShrink
                          label="Address"
                          placeholder="Enter a Location Address"
                          component={AddressField}
                          name="address"
                          vertical
                          showRadius
                          compactSlider
                          autocompleteDisabled={!!location?.timesUsedTotal}
                        />
                      )}
                    </Fragment>,
                    <Fragment key={1}>
                      <div className={cn(classes.section, classes.subSection)}>
                        <Field
                          name="description"
                          label="Location Details"
                          placeholder="Enter any additional information about this location. This is visible to admins only and volunteers will not be able to see this."
                          component={CkEditorStyledField}
                        />
                      </div>
                      <div className={cn(classes.section, classes.subSection)}>
                        <span className={classes.sectionTitle}>Visibility:</span>
                        <RadioGroupFormField
                          name="type"
                          disabled={isSubmitting}
                          options={[
                            {
                              label: 'Allow any volunteer to check into this Location',
                              value: LocationType.Public,
                            },
                          ]}
                        />
                        {values.type === LocationType.Public && (
                          <div className={classes.subItem}>
                            <p className={classes.groupsInfo}>
                              If you would like checking into this Location to confer any Group membership, select the
                              Group(s) below. Volunteers who check in will be sent onboarding questionnaires and waivers
                              needed for Group membership.
                            </p>
                            <Field
                              name="onboardingGroups"
                              placeholder="Select Group (s)"
                              label="Groups"
                              component={StyledAutocomplete}
                              options={onboardingOptions}
                              getOptionLabel={({ name }: EventGroup) => name}
                              disabled={isSubmitting}
                              multiple
                              notched={true}
                            />
                          </div>
                        )}
                        <RadioGroupFormField
                          name="type"
                          disabled={isSubmitting}
                          options={[
                            {
                              label: 'Only allow selected Groups to check into this Location',
                              value: LocationType.Private,
                            },
                          ]}
                        />
                        {values.type === LocationType.Private && (
                          <Field
                            className={classes.subItem}
                            name="visibilityGroups"
                            placeholder="Select Group (s)"
                            label="Groups"
                            component={StyledAutocomplete}
                            options={options}
                            getOptionLabel={({ name, closed, approvedMembersOnly }: EventGroup) =>
                              !closed
                                ? name
                                : approvedMembersOnly
                                ? `${name} - Approved members`
                                : `${name} - Applicants`
                            }
                            disabled={isSubmitting}
                            multiple
                            notched={true}
                          />
                        )}
                      </div>
                      {values.enableCheckIns && (
                        <div className={cn(classes.section, classes.subSection)}>
                          <span className={classes.sectionTitle}>Auto Checkout Time:</span>
                          <FieldArray name="autoCheckoutTimes">
                            {({ push, remove, form: { values: formValues, setFieldTouched } }) => (
                              <div className={classes.section}>
                                {(formValues.autoCheckoutTimes || []).map((_: moment.Moment, index: number) => (
                                  <div className={classes.role} key={index}>
                                    {(formValues.autoCheckoutTimes || []).length > 1 && (
                                      <DeleteIcon className={classes.roleDelete} onClick={() => remove(index)} />
                                    )}
                                    <div className={classes.roleSelector}>
                                      <div className={classes.dateTimePicker}>
                                        <Field
                                          name={`autoCheckoutTimes.${index}`}
                                          inputVariant="outlined"
                                          label="Checkout Time"
                                          higher
                                          component={StyledKeyboardTimePicker}
                                          onBlur={() => setFieldTouched(`autoCheckoutTimes.${index}`, true)}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                <InlineAddButton
                                  onClick={() =>
                                    push(
                                      toOrgWallClockTimeForForm(
                                        DEFAULT_AUTO_CHECKOUT_TIME,
                                        org.timeZone,
                                        fakeAsLocalTimeForUI,
                                      ),
                                    )
                                  }
                                  text="Add Time"
                                />
                              </div>
                            )}
                          </FieldArray>
                        </div>
                      )}
                    </Fragment>,
                  ][screen]
                }
              </DialogContent>
              <DialogActions className={classes.actions}>
                <OutlinedButton
                  disabled={isSubmitting}
                  onClick={() => {
                    if (screen === 0) {
                      close()
                    } else {
                      setScreen(0)
                    }
                  }}
                >
                  {screen === 0 ? 'Cancel' : 'Back'}
                </OutlinedButton>
                <ContainedButton disabled={isSubmitting} isLoading={isSubmitting} onClick={next}>
                  {screen === 0 && values.enableCheckIns ? 'Next' : 'Save'}
                </ContainedButton>
              </DialogActions>
            </Form>
          )
        }}
      </Formik>
    </Dialog>
  )
}

export const AddEditLocationModal = (props: AddEditLocationModalProps) => (
  <ThemeProvider theme={muiTheme}>
    <AddEditLocationDialog {...props} />
  </ThemeProvider>
)

export default AddEditLocationModal
